import React, { JSX } from "react"
import clsx from "clsx"
import { useUserStore } from "../components/stores/userStore"

type BoxProps = {
  as?: React.ElementType
  className?: string
  children?: React.ReactNode
} & Record<string, any>

export const Box = React.forwardRef<HTMLElement, BoxProps>(function Box(
  { as: Component = "div", className, children, ...rest },
  ref,
) {
  return (
    <Component ref={ref as any} className={clsx(className)} {...rest}>
      {children}
    </Component>
  )
})

type TextVariant =
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6"
  | "subtitle1"
  | "subtitle2"
  | "body1"
  | "body2"
  | "caption"
  | "overline"

type TextWeight = "light" | "normal" | "medium" | "semibold" | "bold"

const textVariantClasses: Record<TextVariant, string> = {
  h1: "text-5xl md:text-6xl leading-tight",
  h2: "text-4xl md:text-5xl leading-tight",
  h3: "text-3xl md:text-4xl leading-snug",
  h4: "text-2xl md:text-3xl leading-snug",
  h5: "text-xl md:text-2xl leading-snug",
  h6: "text-lg md:text-xl leading-snug",
  subtitle1: "text-lg leading-normal",
  subtitle2: "text-base leading-normal",
  body1: "text-base leading-relaxed",
  body2: "text-sm leading-relaxed",
  caption: "text-xs leading-relaxed",
  overline: "text-[11px] uppercase tracking-[0.08em]",
}

const textWeightClasses: Record<TextWeight, string> = {
  light: "font-light",
  normal: "font-normal",
  medium: "font-medium",
  semibold: "font-semibold",
  bold: "font-bold",
}

const defaultTagForVariant: Record<TextVariant, keyof JSX.IntrinsicElements> = {
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  h5: "h5",
  h6: "h6",
  subtitle1: "h6",
  subtitle2: "h6",
  body1: "p",
  body2: "p",
  caption: "span",
  overline: "span",
}

type TextProps = {
  variant?: TextVariant
  weight?: TextWeight
  as?: React.ElementType
  className?: string
  children?: React.ReactNode
} & Record<string, any>

export const Text = React.forwardRef<HTMLElement, TextProps>(function Text(
  { variant = "body1", weight = "normal", as, className, children, ...rest },
  ref,
) {
  const Component = as || defaultTagForVariant[variant] || "p"

  return (
    <Component
      ref={ref as any}
      className={clsx("text-foreground", textVariantClasses[variant], textWeightClasses[weight], className)}
      {...rest}
    >
      {children}
    </Component>
  )
})

export function useColorMode() {
  const { mode, setMode } = useUserStore()
  const colorMode = mode === "dark" ? "dark" : "light"

  return {
    colorMode,
    setColorMode: setMode,
  }
}

export const GluestackUIProvider: React.FC<{ config?: any; children: React.ReactNode }> = ({ children }) => {
  const { colorMode } = useColorMode()

  React.useEffect(() => {
    if (typeof document === "undefined") {
      return
    }

    document.documentElement.classList.toggle("dark", colorMode === "dark")
    document.documentElement.dataset.colorMode = colorMode
    document.body.dataset.colorMode = colorMode
  }, [colorMode])

  return <>{children}</>
}
